import { Button, Icon, Tab, TabGroup, Text } from '@nike/eds'
import useQueryState from '../../../common/hooks/useQueryState'
import { useRebookingDeclineReasonConfigTable } from './rebookingdeclinereasonconfigtable/RebookingDeclineReasonConfigTable'
import { TableLayout } from '../../../common/components/table'
import { DeleteModal } from './deletemodal/DeleteModal'
import { useEffect, useMemo, useState } from 'react'
import { RebookingDeclineReasonModal } from './rebookingdeclinereasonmodal/RebookingDeclineReasonModal'
import useRoleContext from '../../../common/hooks/useRoleContext'
import { Navigate } from 'react-router-dom'
import { useDeclineReasons } from '../../../common/hooks/useDeclineReasons'

const pageSizeOptions = [10, 25, 50, 100, 200, 500]
const rebookingTypes = ['REBOOKING', 'HOLD']

const RebookingDeclineReason = () => {
  const [rebookingDeclineReasonConfigModalOpen, setRebookingDeclineReasonConfigModalOpen] =
    useState<boolean>(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const { state: queryState, toggleSortBy } = useQueryState<any>({
    sortBy: [{ id: 'lastUpdatedOn', desc: false }],
  })
  const [selectedRebookingType, setSelectedRebookingType] = useState('REBOOKING')
  const {
    data: declineReasons,
    isSuccess: isDeclineReasonCallSuccess,
    isFetching,
    isError,
    isSuccess,
    refetch,
  } = useDeclineReasons()

  const filteredDeclineReasons = useMemo(() => {
    return isDeclineReasonCallSuccess
      ? declineReasons?.filter(reason => reason.rebookingType === selectedRebookingType)
      : []
  }, [declineReasons, selectedRebookingType, isDeclineReasonCallSuccess])

  useEffect(() => {
    refetch()
  }, [selectedRebookingType, refetch])

  const tableInstance = useRebookingDeclineReasonConfigTable(
    queryState,
    toggleSortBy,
    isFetching,
    isSuccess,
    isError,
    { data: filteredDeclineReasons }
  )

  const handleCloseRebookingDeclineReasonConfigModalForm = () => {
    setRebookingDeclineReasonConfigModalOpen(false)
  }

  const handleCloseDeleteModalForm = () => {
    setDeleteModalOpen(false)
  }

  const hasRebookingReasonsConfigAccess = useRoleContext().hasRebookingReasonConfigAccess()

  if (!hasRebookingReasonsConfigAccess) {
    // no rebooking reasons access - redirect to root and let the router handle the correct redirect
    return <Navigate to="/" replace />
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--eds-space-8)',
        }}
      >
        <div
          className="flex-column flex-nowrap content-space-between justify-items-left"
          style={{ padding: 'var(--eds-space-0) var(--eds-space-16)' }}
        >
          <div
            className="flex-row flex-nowrap content-space-between justify-items-center"
            style={{ padding: 'var(--eds-space-16) var(--eds-space-4)' }}
          >
            <div>
              <Button
                size="medium"
                style={{
                  marginRight: 'var(--eds-space-8)',
                  padding:
                    'var(--eds-space-8) var(--eds-space-16) var(--eds-space-8) var(--eds-space-8)',
                }}
                beforeSlot={<Icon name="Edit" size="m" />}
                onClick={() => setRebookingDeclineReasonConfigModalOpen(true)}
                disabled={tableInstance.selectedFlatRows.length !== 1}
              >
                Edit
              </Button>

              <Button
                size="medium"
                style={{
                  marginRight: 'var(--eds-space-8)',
                  padding:
                    'var(--eds-space-8) var(--eds-space-16) var(--eds-space-8) var(--eds-space-8)',
                }}
                beforeSlot={<Icon name="Delete" size="m" />}
                onClick={() => setDeleteModalOpen(true)}
                disabled={tableInstance.selectedFlatRows.length === 0}
              >
                Delete
              </Button>
            </div>

            <div className="flex-row justify-items-center">
              <Button
                variant="primary"
                className="Button"
                style={{
                  paddingLeft: 'var(--eds-space-16)',
                  padding:
                    'var(--eds-space-8) var(--eds-space-16) var(--eds-space-8) var(--eds-space-8)',
                }}
                beforeSlot={<Icon name="Plus" size="m" />}
                onClick={() => setRebookingDeclineReasonConfigModalOpen(true)}
                disabled={tableInstance.selectedFlatRows.length > 0}
              >
                New
              </Button>
            </div>
          </div>

          <TabGroup
            name="tab-button-group"
            activeId={selectedRebookingType}
            onChange={e => setSelectedRebookingType(e.target.id)}
            variant={undefined}
            style={{ marginLeft: 'var(--eds-space-16)' }}
          >
            {rebookingTypes.map(type => {
              return (
                <Tab id={type} key="tab-1">
                  <Text>{type.charAt(0) + type.slice(1).toLowerCase()}</Text>
                </Tab>
              )
            })}
          </TabGroup>

          <TableLayout
            {...{ ...tableInstance, pageSizeOptions, isFetching, isError }}
            queryState={queryState}
            className="-striped -highlight"
            refetchData={refetch}
            withPagination={false}
            noDataMessage={'No data found. Create a record or refresh the table later'}
          />
        </div>
        <RebookingDeclineReasonModal
          rebookingDeclineReasonModalOpen={rebookingDeclineReasonConfigModalOpen}
          onCloseRebookingDeclineReasonModal={handleCloseRebookingDeclineReasonConfigModalForm}
          selectedRow={tableInstance.selectedFlatRows[0]?.original}
          refetchData={refetch}
          selectedRebookingType={selectedRebookingType}
        />
        <DeleteModal
          deleteModalOpen={deleteModalOpen}
          onCloseDeleteModal={handleCloseDeleteModalForm}
          selectedRows={tableInstance.selectedFlatRows}
          refetchData={refetch}
        />
      </div>
    </>
  )
}

export default RebookingDeclineReason
